import React, { createContext, useState, useEffect } from 'react';
import { API } from '../config/api';

const CartContext = createContext();
const CART_EXPIRY_DAYS = 7; // Cart persists for 7 days

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() => {
    const savedCart = localStorage.getItem('cartItems');
    return savedCart ? JSON.parse(savedCart) : [];
  });
  const [loading, setLoading] = useState(true);
  const [storeSettings, setStoreSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [deliveryZones, setDeliveryZones] = useState([]);

  // Get current delivery zone based on address - MOVE THIS UP
  const getCurrentDeliveryZone = (address = '') => {
    const searchAddress = address || deliveryAddress;
    if (!deliveryZones || !searchAddress) return null;
    
    const activeZones = deliveryZones.filter(zone => zone.isActive);
    for (const zone of activeZones) {
      for (const area of zone.areas) {
        if (searchAddress.toLowerCase().includes(area.toLowerCase())) {
          return zone;
        }
      }
    }
    return null;
  };

  // Fetch store settings and delivery zones
  useEffect(() => {
    const fetchStoreSettings = async () => {
      try {
        const [settingsResponse, zonesResponse] = await Promise.all([
          fetch(`${API}/settings`),
          fetch(`${API}/delivery-zones`)
        ]);

        if (settingsResponse.ok) {
          const settings = await settingsResponse.json();
          setStoreSettings(settings);
        }

        if (zonesResponse.ok) {
          const zonesData = await zonesResponse.json();
          setDeliveryZones(zonesData.deliveryZones || []);
        } else {
          // Set default if fetch fails
          setStoreSettings({
            taxRate: 0,
            defaultDeliveryFee: 1500,
            freeDeliveryThreshold: 10000
          });
          setDeliveryZones([]);
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
        setStoreSettings({
          taxRate: 0,
          defaultDeliveryFee: 1500,
          freeDeliveryThreshold: 10000
        });
        setDeliveryZones([]);
        setSettingsLoading(false);
      } finally {
        setSettingsLoading(false);
      }
    };

    fetchStoreSettings();
  }, []);

  const calculateSubtotal = () => {
    return cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  // Calculate delivery fee based on address
  const calculateDeliveryFee = (address) => {
    const zone = getCurrentDeliveryZone(address);
    if (!zone) return storeSettings?.defaultDeliveryFee || 1500; // default fee
    
    // Check for free delivery threshold
    const currentSubtotal = calculateSubtotal();
    const freeDeliveryThreshold = storeSettings?.freeDeliveryThreshold || 10000;
    
    if (currentSubtotal >= freeDeliveryThreshold) {
      return 0;
    }
    
    return zone.deliveryFee;
  };

  // Update delivery address and recalculate fees
  const updateDeliveryAddress = (address) => {
    setDeliveryAddress(address);
    // Recalculation will happen automatically due to the reactive nature of the context
  };

  // Save to localStorage whenever cart changes
  useEffect(() => {
    localStorage.setItem('cartItems', JSON.stringify(cartItems));
  }, [cartItems]);

  useEffect(() => {
    const loadPersistedCart = async () => {
      try {
        const savedItems = loadCart();
        if (savedItems.length > 0) {
          let validItems = savedItems;
          try {
            validItems = await verifyCartItems(savedItems);
          } catch (err) {
            console.error('Failed to verify cart items:', err);
          }
          setCartItems(validItems);
          if (validItems.length !== savedItems.length) {
            saveCart(validItems);
          }
        } else {
          setCartItems([]); // Explicitly set empty array if no saved items
        }
      } catch (err) {
        console.error('Error loading cart:', err);
        setCartItems([]); // Set empty array on error
      } finally {
        setLoading(false);
      }
    };
    loadPersistedCart();
  }, []);

  const saveCart = (items) => {
    const cartData = {
      items,
      timestamp: new Date().getTime()
    };
    localStorage.setItem('noureeshCart', JSON.stringify(cartData));
  };

  const loadCart = () => {
    const cartData = localStorage.getItem('noureeshCart');
    if (!cartData) return [];
    
    try {
      const parsedData = JSON.parse(cartData);
      const cartAgeDays = (new Date().getTime() - parsedData.timestamp) / (1000 * 60 * 60 * 24);
      
      if (cartAgeDays > CART_EXPIRY_DAYS) {
        localStorage.removeItem('noureeshCart');
        return [];
      }
      
      return parsedData.items || [];
    } catch (error) {
      console.error('Failed to load cart', error);
      return [];
    }
  };

  const verifyCartItems = async (cartItems) => {
    try {
      // Fetch current product data from your API
  const response = await fetch(`${API}/products`);
      if (!response.ok) throw new Error('Failed to fetch products');
      const currentProducts = await response.json();
      
      return cartItems.filter(item => {
        const product = currentProducts.find(p => p._id === item._id);
        return product && product.price === item.price; // Remove if price changed
      });
    } catch (error) {
      console.error('Error verifying cart items:', error);
      return cartItems; // Return original items if verification fails
    }
  };

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    saveCart(cartItems);
  }, [cartItems]);

  const addToCart = (item) => {
    setCartItems(prevItems => {
      // Use _id instead of id for MongoDB
      const existingItem = prevItems.find(i => i._id === item._id);
      
      if (existingItem) {
        // Update quantity if item already exists
        return prevItems.map(i => 
          i._id === item._id ? { ...i, quantity: i.quantity + 1 } : i
        );
      } else {
        // Add new item with proper _id field
        const cartItem = {
          _id: item._id, // Use MongoDB _id
          id: item._id,  // Also keep id for backward compatibility if needed
          name: item.name,
          price: item.price,
          image: item.image,
          quantity: 1
        };
        return [...prevItems, cartItem];
      }
    });
  };

  const updateQuantity = (_id, quantity) => {
    if (quantity < 1) {
      removeFromCart(_id);
      return;
    }
    setCartItems(prevItems =>
      prevItems.map(item =>
        item._id === _id ? { ...item, quantity } : item
      )
    );
  };

  const removeFromCart = (_id) => {
    setCartItems(prevItems => prevItems.filter(item => item._id !== _id));
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const addCustomOrderToCart = (customOrderData) => {
    setCartItems(prevItems => {
      // Generate a unique ID for custom orders (they won't have _id from database)
      const customOrderId = `custom-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      
      const customOrderItem = {
        _id: customOrderId,
        id: customOrderId,
        name: customOrderData.name || `Custom ${customOrderData.orderType} Cake`,
        price: customOrderData.price,
        image: customOrderData.image || '/images/custom-cake-placeholder.jpg',
        quantity: customOrderData.quantity || 1,
        isCustomOrder: true,
        customOrderData: { ...customOrderData } // Store all custom order details
      };
      
      return [...prevItems, customOrderItem];
    });
  };

  // Calculate totals with dynamic settings
  const totalItems = cartItems.reduce((sum, item) => sum + item.quantity, 0);
  const subtotal = calculateSubtotal();
  
  // Use settings from database or fallback to defaults
  const taxRate = storeSettings?.taxRate || 0;
  const deliveryFeeAmount = calculateDeliveryFee(deliveryAddress);
  const freeDeliveryThreshold = storeSettings?.freeDeliveryThreshold || 10000;
  
  const taxAmount = (subtotal * (taxRate / 100));
  const deliveryFee = subtotal >= freeDeliveryThreshold ? 0 : deliveryFeeAmount;
  const computedTotalPrice = subtotal + taxAmount + deliveryFee;

  const contextValue = {
    cartItems,
    loading: loading || settingsLoading,
    storeSettings,
    deliveryAddress,
    deliveryZones,
    updateDeliveryAddress,
    getCurrentDeliveryZone,
    addToCart,
    addCustomOrderToCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    totalItems,
    subtotal,
    taxAmount,
    taxRate,
    deliveryFee,
    freeDeliveryThreshold,
    totalPrice: {
      subtotal,
      taxAmount,
      taxRate,
      deliveryFee,
      totalPrice: computedTotalPrice
    }
  };

  return (
    <CartContext.Provider value={contextValue}>
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;